import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { stringify } from "query-string";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import { hasPermission } from "@/utils/permission-utils";
import {
  defaultTallyConfig,
  mergeRentHallRowsFromSystemHalls,
  normalizeTallyConfig,
  rentHallRowsToRecord,
  type TallyConfig,
  type TallyLagatConfig,
  type TallyRentHallRow,
  type TallySabilTypeKey,
} from "@/types/tally-config";

const SABIL_TYPES: { key: TallySabilTypeKey; label: string }[] = [
  { key: "CHULA", label: "Chula" },
  { key: "ESTABLISHMENT", label: "Establishment" },
  { key: "MUTTAVATTEEN", label: "Muttavatteen" },
  { key: "MUTTAVATTEEN_YEARLY", label: "Muttavatteen Yearly" },
];

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
      {children}
    </Typography>
  );
}

function LagatShapedLedgerFields({
  value,
  canEdit,
  onChange,
}: {
  value: TallyLagatConfig;
  canEdit: boolean;
  onChange: (patch: Partial<TallyLagatConfig>) => void;
}) {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Cash ledger (Dr)"
          value={value.cashLedger}
          onChange={(e) => onChange({ cashLedger: e.target.value })}
          fullWidth
          disabled={!canEdit}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Online / bank ledger (Dr)"
          value={value.bankLedger}
          onChange={(e) => onChange({ bankLedger: e.target.value })}
          fullWidth
          disabled={!canEdit}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Income ledger (Cr)"
          value={value.incomeLedger}
          onChange={(e) => onChange({ incomeLedger: e.target.value })}
          fullWidth
          disabled={!canEdit}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Cost category"
          value={value.costCategory}
          onChange={(e) => onChange({ costCategory: e.target.value })}
          fullWidth
          disabled={!canEdit}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Cost centre"
          value={value.costCentre}
          onChange={(e) => onChange({ costCentre: e.target.value })}
          fullWidth
          disabled={!canEdit}
        />
      </Grid>
    </Grid>
  );
}

export default function TallyConfigPage() {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canEdit = hasPermission(permissions, "tallyConfig.edit");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<TallyConfig>(() => defaultTallyConfig());
  const [rentHalls, setRentHalls] = useState<TallyRentHallRow[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    const hallsQuery = stringify({
      limit: 1000,
      orderBy: "name",
      order: "ASC",
      startAfter: 0,
    });

    Promise.all([
      httpClient(`${getApiUrl()}/tallyConfig`),
      httpClient(`${getApiUrl()}/halls?${hallsQuery}`).catch(() => null),
    ])
      .then(([tallyRes, hallsRes]) => {
        const rows = (tallyRes.json as { rows?: { config?: unknown }[] })?.rows;
        const normalized = normalizeTallyConfig(rows?.[0]?.config);
        setConfig(normalized);

        if (!hallsRes) {
          notify("Could not load halls from the system.", { type: "error" });
          setRentHalls([]);
          return;
        }

        const hallRows = hallsRes.json as { rows?: { name?: string }[] };
        const systemNames = (hallRows.rows ?? [])
          .map((h) => h.name?.trim())
          .filter((n): n is string => Boolean(n));

        setRentHalls(mergeRentHallRowsFromSystemHalls(normalized, systemNames));
      })
      .catch(() => notify("Could not load Tally config", { type: "error" }))
      .finally(() => setLoading(false));
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const save = () => {
    if (!canEdit) return;
    setSaving(true);
    const payload: TallyConfig = {
      ...config,
      rent: { halls: rentHallRowsToRecord(rentHalls) },
    };
    httpClient(`${getApiUrl()}/tallyConfig`, {
      method: "PUT",
      body: JSON.stringify({ config: payload }),
    })
      .then(() => notify("Tally config saved", { type: "success" }))
      .catch(() => notify("Save failed", { type: "error" }))
      .finally(() => setSaving(false));
  };

  const updateRentHall = (index: number, patch: Partial<TallyRentHallRow>) => {
    setRentHalls((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      <Title title="Tally config" />
      <Box sx={{ p: 2, maxWidth: 900 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Ledger and cost centre names must match your Tally company exactly. Each section is
          independent — empty niyaaz ledgers do not block lagat, sabil, or booking exports. Unused
          sabil types and halls may be left blank.
        </Alert>

        <SectionTitle>Lagat</SectionTitle>
        <LagatShapedLedgerFields
          value={config.lagat}
          canEdit={canEdit}
          onChange={(patch) => setConfig((c) => ({ ...c, lagat: { ...c.lagat, ...patch } }))}
        />

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Niyaaz Event</SectionTitle>
        <LagatShapedLedgerFields
          value={config.niyaaz}
          canEdit={canEdit}
          onChange={(patch) => setConfig((c) => ({ ...c, niyaaz: { ...c.niyaaz, ...patch } }))}
        />

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Yearly Niyaaz</SectionTitle>
        <LagatShapedLedgerFields
          value={config.yearlyNiyaaz}
          canEdit={canEdit}
          onChange={(patch) =>
            setConfig((c) => ({ ...c, yearlyNiyaaz: { ...c.yearlyNiyaaz, ...patch } }))
          }
        />

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Sabil</SectionTitle>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cash ledger (Dr)"
              value={config.sabil.cashLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  sabil: { ...c.sabil, cashLedger: e.target.value },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Online / bank ledger (Dr)"
              value={config.sabil.bankLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  sabil: { ...c.sabil, bankLedger: e.target.value },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cost category (shared)"
              value={config.sabil.costCategory}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  sabil: { ...c.sabil, costCategory: e.target.value },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
        {SABIL_TYPES.map(({ key, label }) => (
          <Grid container spacing={2} key={key} sx={{ mb: 1 }}>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Income ledger (Cr)"
                value={config.sabil[key].incomeLedger}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    sabil: {
                      ...c.sabil,
                      [key]: { ...c.sabil[key], incomeLedger: e.target.value },
                    },
                  }))
                }
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Cost centre"
                value={config.sabil[key].costCentre}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    sabil: {
                      ...c.sabil,
                      [key]: { ...c.sabil[key], costCentre: e.target.value },
                    },
                  }))
                }
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
          </Grid>
        ))}

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Rent (per hall)</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Halls are loaded from Setup → Halls. Names must match bookings exactly. Multi-hall
          receipts split amount across halls.
        </Typography>
        {rentHalls.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No halls found. Add halls under Bookings → Setup → Halls, then reload this page.
          </Alert>
        ) : null}
        {rentHalls.map((row, index) => (
          <Grid
            container
            spacing={2}
            key={`hall-${row.hallName}-${index}`}
            sx={{ mb: 2 }}
            alignItems="flex-start"
          >
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                label="Hall name"
                value={row.hallName}
                fullWidth
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                label="Cash ledger (Dr)"
                value={row.cashLedger}
                onChange={(e) => updateRentHall(index, { cashLedger: e.target.value })}
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                label="Online / bank (Dr)"
                value={row.bankLedger}
                onChange={(e) => updateRentHall(index, { bankLedger: e.target.value })}
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                label="Income ledger (Cr)"
                value={row.incomeLedger}
                onChange={(e) => updateRentHall(index, { incomeLedger: e.target.value })}
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 1.75 }}>
              <TextField
                label="Cost category"
                value={row.costCategory}
                onChange={(e) => updateRentHall(index, { costCategory: e.target.value })}
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 1.75 }}>
              <TextField
                label="Cost centre"
                value={row.costCentre}
                onChange={(e) => updateRentHall(index, { costCentre: e.target.value })}
                fullWidth
                disabled={!canEdit}
              />
            </Grid>
          </Grid>
        ))}


        <Divider sx={{ my: 2 }} />
        <SectionTitle>FMB</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Shared cash/bank ledgers. Annual and Contribution each have their own income ledger and
          cost category/centre. Unallocated leftover amounts use the Annual mapping.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cash ledger (Dr)"
              value={config.fmb.cashLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: { ...c.fmb, cashLedger: e.target.value },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Online / bank ledger (Dr)"
              value={config.fmb.bankLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: { ...c.fmb, bankLedger: e.target.value },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
          Annual (takhmeen)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Income ledger (Cr)"
              value={config.fmb.annual.incomeLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    annual: { ...c.fmb.annual, incomeLedger: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cost category"
              value={config.fmb.annual.costCategory}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    annual: { ...c.fmb.annual, costCategory: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cost centre"
              value={config.fmb.annual.costCentre}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    annual: { ...c.fmb.annual, costCentre: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
          Contribution
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Income ledger (Cr)"
              value={config.fmb.contribution.incomeLedger}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    contribution: { ...c.fmb.contribution, incomeLedger: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cost category"
              value={config.fmb.contribution.costCategory}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    contribution: { ...c.fmb.contribution, costCategory: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Cost centre"
              value={config.fmb.contribution.costCentre}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  fmb: {
                    ...c.fmb,
                    contribution: { ...c.fmb.contribution, costCentre: e.target.value },
                  },
                }))
              }
              fullWidth
              disabled={!canEdit}
            />
          </Grid>
        </Grid>

        {canEdit ? (
          <Box>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Box>
        ) : (
          <Alert severity="warning">You do not have permission to edit Tally config.</Alert>
        )}
      </Box>
    </>
  );
}
